import { CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE } from '../base/conference/actionTypes';
import { getParticipantCount } from '../base/participants/functions';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { SET_REQUESTING_SUBTITLES } from '../subtitles/actionTypes';
import { setRequestingSubtitles } from '../subtitles/actions.any';
import { SET_AI_NOTES_PANEL_VISIBLE, START_AI_NOTES_AUTO_SUMMARY, STOP_AI_NOTES_POLL } from './actionTypes';
import { isWeChatMiniProgramWebView } from '../portal/functions';
import { clearAiNotes, finalizeAiNotes, refreshPartialAiNotes, setAiNotesNextSummaryAt } from './actions';
import { AI_NOTES_AUTO_SUMMARY_INTERVAL_MS, AI_NOTES_FEATURE_KEY, AI_NOTES_FINALIZE_MAX_PARTICIPANTS } from './constants';
import { isAiNotesEnabled } from './functions';
import logger from './logger';
let autoSummaryTimer;
/** 离会前快照：仅当会场只剩本地一人时才自动 finalize */
let shouldFinalizeOnLeave = false;
/**
 * 停止自动总结定时器.
 *
 * @param {Function} [dispatch] - Optional dispatch to clear nextSummaryAt.
 * @returns {void}
 */
function stopAiNotesAutoSummary(dispatch) {
    if (autoSummaryTimer) {
        clearTimeout(autoSummaryTimer);
        autoSummaryTimer = undefined;
        logger.info('AI 纪要自动总结定时器已停止');
    }
    if (dispatch) {
        dispatch(setAiNotesNextSummaryAt(null));
    }
}
/**
 * 启动/重置 2 分钟自动总结.
 *
 * @param {Object} store - Redux store.
 * @returns {void}
 */
function scheduleAiNotesAutoSummary(store) {
    stopAiNotesAutoSummary();
    const state = store.getState();
    if (!isAiNotesEnabled(state)) {
        return;
    }
    const requesting = Boolean(state['features/subtitles']?._requestingSubtitles);
    if (!requesting) {
        store.dispatch(setAiNotesNextSummaryAt(null));
        return;
    }
    const nextAt = Date.now() + AI_NOTES_AUTO_SUMMARY_INTERVAL_MS;
    store.dispatch(setAiNotesNextSummaryAt(nextAt));
    logger.info(`AI 纪要将于 ${AI_NOTES_AUTO_SUMMARY_INTERVAL_MS}ms 后自动总结`);
    autoSummaryTimer = setTimeout(() => {
        autoSummaryTimer = undefined;
        const latest = store.getState();
        if (!isAiNotesEnabled(latest)) {
            return;
        }
        if (!latest['features/subtitles']?._requestingSubtitles) {
            store.dispatch(setAiNotesNextSummaryAt(null));
            return;
        }
        logger.info('AI 纪要自动增量总结触发');
        // 自动总结不 force：无新发言时后端跳过并继续监听
        Promise.resolve(store.dispatch(refreshPartialAiNotes({ force: false })))
            .catch((err) => {
            logger.warn('自动增量总结失败', err);
        })
            .finally(() => {
            scheduleAiNotesAutoSummary(store);
        });
    }, AI_NOTES_AUTO_SUMMARY_INTERVAL_MS);
}
/**
 * AI 纪要：更多菜单打开面板；2 分钟自动总结；离会 finalize 策略.
 */
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    switch (action.type) {
        case STOP_AI_NOTES_POLL: {
            stopAiNotesAutoSummary(store.dispatch);
            break;
        }
        case START_AI_NOTES_AUTO_SUMMARY: {
            scheduleAiNotesAutoSummary(store);
            break;
        }
        case SET_AI_NOTES_PANEL_VISIBLE: {
            if (action.visible && isAiNotesEnabled(store.getState())) {
                const state = store.getState();
                const requesting = Boolean(state['features/subtitles']?._requestingSubtitles);
                if (!requesting) {
                    // 第二参 displaySubtitles：手机/小程序需要同屏看到字幕，电脑侧栏可只收转写
                    const showOnStage = isWeChatMiniProgramWebView();
                    store.dispatch(setRequestingSubtitles(true, showOnStage, null));
                }
                if (!state[AI_NOTES_FEATURE_KEY]?.nextSummaryAt) {
                    scheduleAiNotesAutoSummary(store);
                }
            }
            break;
        }
        case SET_REQUESTING_SUBTITLES: {
            const state = store.getState();
            if (!isAiNotesEnabled(state)) {
                break;
            }
            if (action.enabled) {
                scheduleAiNotesAutoSummary(store);
            }
            else {
                stopAiNotesAutoSummary(store.dispatch);
            }
            break;
        }
        case CONFERENCE_JOINED: {
            stopAiNotesAutoSummary(store.dispatch);
            // 小程序窄屏原先难找到 CC/AI 入口，进会即拉起转写，避免「电脑有、手机没有」
            if (isWeChatMiniProgramWebView() && isAiNotesEnabled(store.getState())) {
                const joinedState = store.getState();
                const requesting = Boolean(joinedState['features/subtitles']?._requestingSubtitles);
                if (!requesting) {
                    logger.info('微信小程序进会：自动开启字幕转写（供字幕与 AI 纪要）');
                    store.dispatch(setRequestingSubtitles(true, true, null));
                }
                scheduleAiNotesAutoSummary(store);
            }
            break;
        }
        case CONFERENCE_WILL_LEAVE: {
            const state = store.getState();
            if (!isAiNotesEnabled(state)) {
                shouldFinalizeOnLeave = false;
                break;
            }
            const participantCount = getParticipantCount(state);
            shouldFinalizeOnLeave = participantCount <= AI_NOTES_FINALIZE_MAX_PARTICIPANTS;
            logger.info(`AI 纪要离会前置检查 | participants=${participantCount}, autoFinalize=${shouldFinalizeOnLeave}`);
            break;
        }
        case CONFERENCE_LEFT: {
            stopAiNotesAutoSummary(store.dispatch);
            const state = store.getState();
            const autoFinalize = shouldFinalizeOnLeave;
            shouldFinalizeOnLeave = false;
            if (isAiNotesEnabled(state) && autoFinalize) {
                Promise.resolve(store.dispatch(finalizeAiNotes()))
                    .catch((err) => {
                    logger.warn('离会自动生成总纪要失败', err);
                })
                    .finally(() => {
                    store.dispatch(clearAiNotes());
                });
            }
            else {
                store.dispatch(clearAiNotes());
            }
            break;
        }
    }
    return result;
});
